import { useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ChevronDown, ChevronUp } from "lucide-react";

const STATUTS = {
  nouveau: { label: "Nouveau", cls: "bg-clay text-cream" },
  a_rappeler: { label: "À rappeler", cls: "bg-sand text-ink" },
  traite: { label: "Traité", cls: "bg-ink/10 text-bark" },
};

const DevisPhoto = ({ devisId, fname }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let url;
    api
      .get(`/admin/devis/${devisId}/photos/${fname}`, { responseType: "blob" })
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        setSrc(url);
      })
      .catch(() => setSrc("error"));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [devisId, fname]);

  if (src === "error") return null;
  if (!src) {
    return <div className="aspect-[4/3] w-32 animate-pulse bg-plaster" aria-label="Chargement de la photo" />;
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" data-testid={`admin-devis-photo-${fname}`} className="block w-32 overflow-hidden">
      <img src={src} alt="Photo transmise par le client" className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105" />
    </a>
  );
};

const AdminDevis = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () =>
    api
      .get("/admin/devis")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Impossible de charger les demandes"));

  useEffect(() => {
    load();
  }, []);

  const setStatut = async (d, statut) => {
    try {
      await api.patch(`/admin/devis/${d.id}/statut`, { statut });
      setItems((list) => list.map((x) => (x.id === d.id ? { ...x, statut } : x)));
    } catch {
      toast.error("Impossible de changer le statut");
    }
  };

  if (!items.length) {
    return <p className="text-bark" data-testid="admin-devis-empty">Aucune demande de devis pour le moment.</p>;
  }

  return (
    <div className="space-y-4" data-testid="admin-devis-list">
      <h2 className="font-serif text-2xl tracking-tight">Demandes de devis ({items.length})</h2>
      {items.map((d) => {
        const statut = STATUTS[d.statut] || STATUTS.nouveau;
        return (
          <div key={d.id} className="bg-white/60 p-5" data-testid={`admin-devis-${d.id}`}>
            <button
              className="flex w-full items-center justify-between gap-4 text-left"
              onClick={() => setOpen(open === d.id ? null : d.id)}
              data-testid={`admin-devis-toggle-${d.id}`}
            >
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <span className={`rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wider ${statut.cls}`} data-testid={`admin-devis-statut-${d.id}`}>
                  {statut.label}
                </span>
                <div className="min-w-0">
                  <p className="truncate font-medium">{d.nom} <span className="text-taupe">— {d.ville}</span></p>
                  <p className="mt-1 text-sm text-taupe">
                    {d.prestation} · {new Date(d.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                </div>
              </div>
              {open === d.id ? <ChevronUp size={18} className="shrink-0 text-taupe" /> : <ChevronDown size={18} className="shrink-0 text-taupe" />}
            </button>
            {open === d.id && (
              <div className="mt-5 space-y-3 border-t border-ink/10 pt-5 text-sm">
                <p className="flex items-center gap-2"><Phone size={14} className="text-clay" /> <a href={`tel:${d.telephone}`} className="hover:text-clay">{d.telephone}</a></p>
                <p className="flex items-center gap-2"><Mail size={14} className="text-clay" /> <a href={`mailto:${d.email}`} className="hover:text-clay">{d.email}</a></p>
                <p className="flex items-center gap-2"><MapPin size={14} className="text-clay" /> {d.ville}</p>
                <p className="whitespace-pre-line leading-relaxed text-bark">{d.description}</p>
                {d.photos?.length > 0 && (
                  <div className="pt-2">
                    <p className="mb-3 text-xs uppercase tracking-[0.2em] text-taupe">Photos du chantier (cliquer pour agrandir)</p>
                    <div className="flex flex-wrap gap-3">
                      {d.photos.map((f) => (
                        <DevisPhoto key={f} devisId={d.id} fname={f} />
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 border-t border-ink/10 pt-4">
                  <span className="mr-2 text-xs uppercase tracking-[0.2em] text-taupe">Suivi :</span>
                  {Object.entries(STATUTS).map(([key, s]) => (
                    <button
                      key={key}
                      onClick={() => setStatut(d, key)}
                      disabled={(d.statut || "nouveau") === key}
                      data-testid={`admin-devis-set-${key}-${d.id}`}
                      className={`rounded-full px-4 py-2 text-xs transition-opacity duration-200 ${s.cls} ${(d.statut || "nouveau") === key ? "cursor-default opacity-40" : "hover:opacity-80"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminDevis;
